<template>
  <div class="w-100">
    <basic-subheader>
      <template v-slot:actions>
        <b-row>
          <b-col>
            <div class="d-flex justify-content-end">
              <b-button
                v-if="isInsertPermission"
                class="btn btn-success"
                type="button"
                @click="openCreateForm"
              >
                <span class="svg-icon">
                  <inline-svg src="/media/svg/icons/Neolex/Basic/plus.svg" />
                </span>
                Thêm mới
              </b-button>
            </div>
          </b-col>
        </b-row>
      </template>
    </basic-subheader>
    <Wrapper>
      <b-overlay :show="loading">
        <b-row class="mb-4">
          <b-col md="4">
            <basic-input
              placeholder="Tìm theo tên phòng ban"
              :value.sync="searchName"
              @keyup.enter.native="loadData"
            />
          </b-col>
          <b-col md="2">
            <b-button class="btn btn-primary" @click="loadData">
              <span class="svg-icon">
                <inline-svg src="/media/svg/icons/Neolex/Basic/search.svg" />
              </span>
              Tìm kiếm
            </b-button>
          </b-col>
        </b-row>

        <div v-if="departments.length === 0 && !loading" class="text-center py-8 text-muted">
          Không có phòng ban nào.
        </div>

        <div
          v-for="dept in departments"
          :key="dept.id"
          class="department-card mb-4"
        >
          <b-card no-body>
            <b-card-header
              header-tag="div"
              class="department-header d-flex align-items-center justify-content-between"
              role="button"
              @click="toggleDept(dept.id)"
            >
              <div class="d-flex align-items-center">
                <v-btn icon class="mr-2">
                  <v-icon>
                    mdi-{{ openDepts.includes(dept.id) ? 'chevron-down' : 'chevron-right' }}
                  </v-icon>
                </v-btn>
                <div>
                  <span class="font-weight-bold">{{ dept.name }}</span>
                  <span class="text-muted ml-3" style="font-size:12px">{{ dept.code }}</span>
                  <span
                    v-if="dept.description"
                    class="text-muted ml-3"
                    style="font-size:12px"
                  >— {{ dept.description }}</span>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <b-badge variant="secondary" class="mr-3">
                  {{ dept.roles ? dept.roles.length : 0 }} role
                </b-badge>
                <action-dropdown
                  :value="dept"
                  :show_copy="false"
                  :show_view="false"
                  :show_edit="isUpdatePermission"
                  :show_delete="isDeletePermission"
                  @edit="openEditForm(dept)"
                  @delete="confirmDelete(dept)"
                >
                  <b-dropdown-text
                    v-if="isAssignPermission"
                    tag="div"
                    class="navi-item cursor-pointer"
                    @click.stop="openRoleMapping(dept)"
                  >
                    <a class="navi-link cursor-pointer">
                      <span class="menu-icon svg-icon svg-icon-sm text-primary">
                        <inline-svg src="/media/svg/icons/Neolex/Basic/plus.svg" />
                      </span>
                      <span class="navi-text text-primary">Gán role</span>
                    </a>
                  </b-dropdown-text>
                </action-dropdown>
              </div>
            </b-card-header>
            <b-collapse :visible="openDepts.includes(dept.id)">
              <b-card-body>
                <div v-if="dept.roles && dept.roles.length" class="d-flex flex-wrap">
                  <b-badge
                    v-for="role in dept.roles"
                    :key="role.id"
                    variant="light-primary"
                    class="mr-2 mb-2 p-2"
                    style="font-size:13px; border: 1px solid #e0e0e0"
                  >
                    {{ role.roleName }}
                  </b-badge>
                </div>
                <div v-else class="text-muted">Chưa có role nào.</div>
              </b-card-body>
            </b-collapse>
          </b-card>
        </div>
      </b-overlay>
    </Wrapper>

    <DepartmentForm
      :department="selectedDepartment"
      @save-success="onSaveSuccess"
      @reset="selectedDepartment = null"
    />
    <DepartmentRoleMapping
      :department="activeMappingDept"
      :all-roles="allRoles"
      @mapping-changed="onMappingChanged"
    />
  </div>
</template>
<script>
export default {
  name: 'DepartmentPage',
  components: {
    DepartmentForm: () => import('./components/DepartmentForm.vue'),
    DepartmentRoleMapping: () =>
      import('./components/DepartmentRoleMapping.vue'),
  },
  data() {
    return {
      loading: false,
      departments: [],
      allRoles: [],
      searchName: null,
      openDepts: [],
      selectedDepartment: null,
      activeMappingDept: null,
    };
  },
  created() {
    this.loadData();
    this.loadRoles();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const params = {};
        if (this.searchName) params.Name = this.searchName;
        const { meta, error, data } = await this.$api.get('/Department', {
          params,
        });
        if (!meta.success) {
          return this.$toastr.e({
            title: 'Lỗi !',
            msg: error.message,
            preventDuplicates: true,
          });
        }
        this.departments = data || [];
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message,
          preventDuplicates: true,
        });
      } finally {
        this.loading = false;
      }
    },
    async loadRoles() {
      try {
        const { meta, data } = await this.$api.get('/Role');
        if (meta.success) {
          this.allRoles = data || [];
        }
      } catch (_) {
        // roles are supplementary; silence failure
      }
    },
    toggleDept(id) {
      const idx = this.openDepts.indexOf(id);
      if (idx >= 0) {
        this.openDepts.splice(idx, 1);
      } else {
        this.openDepts.push(id);
      }
    },
    openCreateForm() {
      this.selectedDepartment = null;
      this.$bvModal.show('modal-department-form');
    },
    openEditForm(dept) {
      this.selectedDepartment = dept;
      this.$bvModal.show('modal-department-form');
    },
    openRoleMapping(dept) {
      this.activeMappingDept = dept;
      this.$bvModal.show('modal-department-role-mapping');
    },
    onSaveSuccess() {
      this.selectedDepartment = null;
      this.loadData();
    },
    async onMappingChanged() {
      await this.loadData();
      if (this.activeMappingDept) {
        const updated = this.departments.find(
          (d) => d.id === this.activeMappingDept.id,
        );
        if (updated) this.activeMappingDept = { ...updated };
      }
    },
    confirmDelete(dept) {
      this.$swal({
        title: '',
        text: `Bạn có chắc muốn xóa phòng ban "${dept.name}" không?`,
        icon: '/media/svg/icons/SweetAlert/alert-triangle-red.svg',
        buttons: {
          cancel: {
            text: 'Hủy',
            value: false,
            visible: true,
            className: 'btn btn-secondary',
            closeModal: true,
          },
          confirm: {
            text: 'Chắc chắn',
            value: true,
            visible: true,
            className: 'btn btn-inactive',
            closeModal: true,
          },
        },
      }).then(
        function (result) {
          if (result) {
            this.deleteDepartment(dept);
          }
        }.bind(this),
      );
    },
    async deleteDepartment(dept) {
      try {
        this.loading = true;
        const { meta, error } = await this.$api.delete(
          `/Department/${dept.id}`,
        );
        if (!meta.success) {
          return this.$toastr.e({
            title: 'Lỗi !',
            msg: error.message,
            preventDuplicates: true,
          });
        }
        this.$toastr.s({
          title: 'Thành công !',
          msg: 'Xóa phòng ban thành công',
        });
        this.loadData();
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message,
          preventDuplicates: true,
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.department-header {
  background: #f8f9fa;
  cursor: pointer;
  padding: 12px 16px;
  &:hover {
    background: #e9ecef;
  }
}
</style>
